import { useDeferredValue, useEffect, useMemo, useReducer } from 'react';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import {
  buildResolvedVersionEnrichContext,
  getProposalElementRollupContext,
} from '@shared/helpers/actions/resolved-version.helpers';
import {
  applyMarginalsToAttributedKpiDeltas,
  computeAttributedPerActionKpiDeltas,
  createSequentialMarginalKpiStepper,
  proposalNeedsSequentialKpiSignCorrection,
  type PerActionKpiPrefixReadOptions,
  type SequentialMarginalKpiStepper,
} from '@shared/helpers/actions/per-action-proposal-kpi.helpers';
import { getResolvedVersionEnrichCacheDeps } from '@shared/helpers/actions/resolved-version-enrich-cache.helpers';
import {
  filterResultsByScope,
  getResultsScopeCacheKey,
} from '@shared/helpers/results-scope.helpers';
import type { ProposalId } from '@shared/models/proposals.model';
import { useProject } from '@client/store/project';
import { useSelectedVersion } from '@client/store/ui';
import { useGetResultsPerGFA } from '@features/results/use-results.hooks';
import { useProposalsResultRecords } from '../use-proposals.hooks';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useDataTables } from '@features/data-table/store/data-table-store.hooks';
import { usePvgisEnvironmentHourData } from '@client/store/pvgis/pvgis.hook';
import { getEmptyFrozenArray } from '@shared/helpers/array.helpers';
import { useSelectedLifecycles } from '@features/lifecycles/LifecyclesSelect';
import { useEffectiveSbefGroups } from '@features/sbef/use-effective-sbef-groups.hook';
import { useConservativeGenericCarbon } from '@features/results/conservative-generic-carbon.hook';
import { useProposal } from './use-proposals.hooks';
import { useActionsForProposal } from './use-actions-for-proposal.hooks';
import {
  readCachedSequentialMarginalKpis,
  scheduleSequentialMarginalKpis,
} from './per-action-kpi-marginals.helpers';

export type PerActionProposalKpiDeltas = {
  /** Per-action share of KPI1 (CO2e delta vs baseline), same total as the carbon KPI card. */
  co2eByActionId: Readonly<Record<string, number>> | null;
  /** Per-action share of KPI2 (cost delta); `null` when cost KPIs are disabled. */
  sekByActionId: Readonly<Record<string, number>> | null;
  /**
   * True while the sign-corrected marginal series runs off the render path
   * (DEV-3703); the per-action values land once it completes.
   */
  isComputingMarginals: boolean;
};

const EMPTY_DELTAS: PerActionProposalKpiDeltas = {
  co2eByActionId: null,
  sekByActionId: null,
  isComputingMarginals: false,
};

const COMPUTING_DELTAS: PerActionProposalKpiDeltas = {
  ...EMPTY_DELTAS,
  isComputingMarginals: true,
};

type PendingMarginals = {
  cacheKey: string;
  cacheDeps: readonly unknown[];
  createStepper: () => SequentialMarginalKpiStepper;
};

/**
 * Per-action shares of proposal-vs-baseline KPI1 (CO2e) and KPI2 (cost).
 * Element-attributed splits from enriched results; prefix marginals correct sign
 * and magnitude after recipe apply flows (DEV-3381, DEV-3389).
 *
 * The prefix series runs one compute-pipeline pass per action, so it runs off the
 * render path via {@link scheduleSequentialMarginalKpis} — the hook returns empty
 * deltas until the series lands in the marginals cache, then re-renders with the
 * sign-corrected values (DEV-3703). Result-scope inputs are deferred so the
 * lifecycle/SBEF control can paint before the attributed split re-runs.
 */
export const usePerActionProposalKpiDeltas = (
  proposalId: ProposalId | undefined,
): PerActionProposalKpiDeltas => {
  const project = useProject();
  const version = useSelectedVersion();
  const proposal = useProposal(proposalId);
  const actions = useActionsForProposal(proposalId);
  const getResultsPerGFA = useGetResultsPerGFA();
  // Defer scope with the already-scoped records so the selector paints first and
  // prefix compute does not mix a new resultScope with the previous records.
  const selectedLifecycles = useDeferredValue(useSelectedLifecycles());
  const effectiveSbefGroups = useDeferredValue(useEffectiveSbefGroups());
  const conservative = useDeferredValue(useConservativeGenericCarbon());
  const records = useDeferredValue(useProposalsResultRecords());
  const displayCurrency = useDisplayCurrency();
  const dataTables = useDataTables();
  const environmentHourData = usePvgisEnvironmentHourData();
  const [marginalsGeneration, onMarginalsComplete] = useReducer(
    (count: number) => count + 1,
    0,
  );

  const computed = useMemo((): {
    deltas: PerActionProposalKpiDeltas;
    pendingMarginals: PendingMarginals | null;
  } => {
    // Re-runs this memo when the scheduled series completes, so the cache
    // read below picks up the freshly stored marginals.
    void marginalsGeneration;
    const done = (
      deltas: Omit<PerActionProposalKpiDeltas, 'isComputingMarginals'>,
    ) => ({
      deltas: { ...deltas, isComputingMarginals: false },
      pendingMarginals: null,
    });
    if (!version || !proposal || !proposalId || !project)
      return done(EMPTY_DELTAS);
    if (actions.length === 0) return done(EMPTY_DELTAS);

    const resultScope = {
      lifecycles: selectedLifecycles,
      sbefGroups: effectiveSbefGroups,
      conservative,
    };
    const baselineRec = records[version.id] ?? {};
    const proposalRec = records[proposalId] ?? {};
    const baselineTotal = getResultsPerGFA(baselineRec[version.id] ?? {});
    const proposalTotal = getResultsPerGFA(proposalRec[version.id] ?? {});
    const baselineCo2 = getConversionFactorValue(baselineTotal, 'co2e');
    const proposalCo2 = getConversionFactorValue(proposalTotal, 'co2e');
    const kpiCo2Delta = proposalCo2 - baselineCo2;

    const versionActions = version.actions ?? [];
    const enrichContext = buildResolvedVersionEnrichContext(project, {
      weatherHours: environmentHourData ?? getEmptyFrozenArray(),
      dataTables,
    });
    const rollupContext = getProposalElementRollupContext(
      version,
      proposalId,
      enrichContext,
    );
    const baselineCost = getConversionFactorValue(baselineTotal, 'sek');
    const proposalCost = getConversionFactorValue(proposalTotal, 'sek');
    const kpiCostDelta = displayCurrency.isEnabled
      ? proposalCost - baselineCost
      : null;

    const attributed = computeAttributedPerActionKpiDeltas({
      project,
      version,
      proposal,
      actions: versionActions,
      baselineRecord: baselineRec,
      proposalRecord: proposalRec,
      rollupContext,
      kpiCo2Delta,
      kpiSekDelta: kpiCostDelta,
    });

    if (!proposalNeedsSequentialKpiSignCorrection(proposal, versionActions)) {
      return done(attributed);
    }

    // Same invalidation inputs as the DEV-3669 cacheFactory key: no `version` /
    // `project` object identity and no callback identities — those change on URL
    // sync and store clones without new pipeline inputs.
    const cacheKey = `${version.id}:${proposal.id}`;
    const cacheDeps: readonly unknown[] = [
      version.actions,
      proposal.actionIds,
      version.resultsRecord,
      getResultsScopeCacheKey(resultScope),
      ...getResolvedVersionEnrichCacheDeps(project),
      dataTables,
      environmentHourData,
    ];
    const prefixReadOptions: PerActionKpiPrefixReadOptions = {
      // Stored (unscoped) baseline: filterResultsByScope must run once. The
      // attributed records above are already scoped; feeding them in again
      // zeros prefix 0 when an SBEF subset has stripped group buckets.
      baselineRecord: version.resultsRecord ?? {},
      filterResults: (results) => filterResultsByScope(results, resultScope),
      normalizePerGfa: getResultsPerGFA,
      // The prefix series runs the real compute pipeline, so it needs the
      // same enrich dependencies as the main pipeline (DEV-3646, same
      // contract as DEV-3503).
      computeDependencies: {
        dataTables,
        weatherHours: environmentHourData ?? getEmptyFrozenArray(),
      },
    };

    const marginals = readCachedSequentialMarginalKpis(cacheKey, cacheDeps);
    // Failed series: leave the loading state (no reschedule until deps change)
    // and show dashes — attributed shares can carry the wrong sign (DEV-3381).
    if (marginals === 'failed') return done(EMPTY_DELTAS);
    if (marginals !== 'missing') {
      return done(
        applyMarginalsToAttributedKpiDeltas(attributed, marginals, {
          actionIds: proposal.actionIds,
          kpiCo2Delta,
          kpiSekDelta: kpiCostDelta,
        }),
      );
    }

    // Empty (not attributed) while the series runs: attributed shares can carry the
    // wrong sign here — showing them for the whole chunked run would re-surface the
    // DEV-3381 bug on every visit.
    return {
      deltas: COMPUTING_DELTAS,
      pendingMarginals: {
        cacheKey,
        cacheDeps,
        createStepper: () =>
          createSequentialMarginalKpiStepper(
            project,
            version,
            proposal,
            prefixReadOptions,
          ),
      },
    };
  }, [
    displayCurrency.isEnabled,
    project,
    version,
    proposal,
    proposalId,
    actions,
    records,
    getResultsPerGFA,
    selectedLifecycles,
    effectiveSbefGroups,
    conservative,
    dataTables,
    environmentHourData,
    marginalsGeneration,
  ]);

  const { pendingMarginals } = computed;

  useEffect(() => {
    if (!pendingMarginals) return;
    return scheduleSequentialMarginalKpis(
      pendingMarginals.cacheKey,
      pendingMarginals.cacheDeps,
      pendingMarginals.createStepper,
      onMarginalsComplete,
    );
  }, [pendingMarginals]);

  return computed.deltas;
};
